// Carrier + ship method (Ground / Next Day / 2 Day / 3 Day) as two drop-downs
// that compose one stored value like "UPS Ground" (Stephanie 2026-10-14).
// An older free-text value that doesn't fit stays selectable, so editing a
// line never silently loses what was there.
//
// "+ Add a method…" (Stephanie 2026-10-14: "add the ability to add ship methods
// in the drop down"): a typed method is saved with the item, and every method
// already saved anywhere (GET /ship-methods) is offered to everyone.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PARCEL_CARRIERS, SHIP_SERVICE_LEVELS, joinShipMethod, splitShipMethod } from "@/lib/carriers";

export function ShipMethodPicker({
  value,
  onChange,
  selectClassName,
}: {
  value: string;
  onChange: (next: string) => void;
  selectClassName: string;
}) {
  const { carrier, level } = splitShipMethod(value);
  const inUse = useQuery({
    queryKey: ["ship-methods"],
    queryFn: () => api<{ rows: string[] }>("/ship-methods"),
    staleTime: 5 * 60_000,
  });
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");

  const standard: readonly string[] = SHIP_SERVICE_LEVELS;
  const added = [
    ...new Set(
      (inUse.data?.rows ?? [])
        .map((m) => splitShipMethod(m).level)
        .filter((l) => l !== "" && !standard.some((s) => s.toLowerCase() === l.toLowerCase())),
    ),
  ].sort((a, b) => a.localeCompare(b));
  const levels = [...standard, ...added];

  return (
    <span className="mt-1 grid grid-cols-2 gap-2">
      <select
        aria-label="Carrier"
        value={carrier}
        onChange={(e) => onChange(joinShipMethod(e.target.value, level))}
        className={selectClassName}
      >
        <option value="">Carrier…</option>
        {PARCEL_CARRIERS.map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
      </select>
      {adding ? (
        <span className="flex items-center gap-1">
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (draft.trim()) onChange(joinShipMethod(carrier, draft.trim()));
                setAdding(false);
                setDraft("");
              } else if (e.key === "Escape") {
                setAdding(false);
                setDraft("");
              }
            }}
            maxLength={60}
            placeholder="e.g. Saturday Delivery"
            aria-label="New ship method"
            className={selectClassName}
          />
          <button
            type="button"
            disabled={draft.trim() === ""}
            onClick={() => {
              onChange(joinShipMethod(carrier, draft.trim()));
              setAdding(false);
              setDraft("");
            }}
            className="rounded-md bg-ink px-2 py-1 text-xs font-semibold text-white disabled:opacity-50"
          >
            Add
          </button>
        </span>
      ) : (
        <select
          aria-label="Ship method"
          value={level}
          onChange={(e) => {
            if (e.target.value === "__add") {
              setAdding(true);
              return;
            }
            onChange(joinShipMethod(carrier, e.target.value));
          }}
          className={selectClassName}
        >
          <option value="">Method…</option>
          {levels.map((l) => (
            <option key={l} value={l}>{l}</option>
          ))}
          {level && !levels.includes(level) && <option value={level}>{level}</option>}
          <option value="__add">+ Add a method…</option>
        </select>
      )}
    </span>
  );
}
